import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import AdminLoggedIn from "./admin-logged-in";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { fetchAdminData } from "../../redux/thunks/thunk-users";
import { AppDispatch } from "../../redux/store";

// Update your Cognito User Pool details.
const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};

const userPool = new CognitoUserPool(poolData);

export default function Admin() {
  const dispatch = useDispatch<AppDispatch>();

  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthorizedAdmin, setIsAuthorizedAdmin] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>("");

  // Check if a user is already logged in.
  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (session && session.isValid()) {
          currentUser.getUserAttributes(
            (err: any, attributes?: CognitoUserAttribute[]) => {
              if (err) {
                setError(err.message || JSON.stringify(err));
                return;
              }
              if (attributes) {
                const emailAttr = attributes.find(
                  (attr: CognitoUserAttribute) => attr.getName() === "email"
                );
                const email = emailAttr ? emailAttr.getValue() : "";
                setUserEmail(email);
                setIsAuthenticated(true);
                const isAdmin =
                  email === "olimjon.nematov@gmail.com" ||
                  email === "Adbyerly@verizon.net" ||
                  email === "nickiepatel@gmail.com";
                setIsAuthorizedAdmin(isAdmin);
                if (isAdmin) {
                  const idToken = session.getIdToken().getJwtToken();
                  // Dispatch the thunk to call the admin API endpoint.
                  dispatch(fetchAdminData(idToken));
                }
              }
            }
          );
        }
      });
    }
  }, [dispatch]);

  // Handle login form submission.
  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const userData = {
      Username: username,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);
    cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        cognitoUser.getUserAttributes(
          (err: any, attributes?: CognitoUserAttribute[]) => {
            if (err) {
              setError(err.message || JSON.stringify(err));
              return;
            }

            if (!attributes) {
              setError("No attributes found");
              return;
            }

            const emailAttr = attributes.find(
              (attr: CognitoUserAttribute) => attr.getName() === "email"
            );
            const email = emailAttr ? emailAttr.getValue() : "";
            setUserEmail(email);
            setIsAuthenticated(true);
            const isAdmin = email === "olimjon.nematov@gmail.com";
            setIsAuthorizedAdmin(isAdmin);
            if (isAdmin) {
              const idToken = session.getIdToken().getJwtToken();
              // Dispatch the thunk to fetch admin-specific data.
              dispatch(fetchAdminData(idToken));
            }
          }
        );
      },
      onFailure: (err: any) => {
        setError(err.message || JSON.stringify(err));
      },
    });
  };

  const handleLogout = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    // Reset state.
    setIsAuthenticated(false);
    setIsAuthorizedAdmin(false);
    setUsername("");
    setPassword("");
    setUserEmail("");
  };

  // Render the login form if not authenticated.
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ padding: 3, marginTop: 8 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Login
          </Typography>
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              columns: 5,
            }}
          >
            <TextField
              label="Username"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" variant="contained" color="primary">
              Login
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Container>
    );
  }

  // If authenticated but not an authorized admin.
  if (!isAuthorizedAdmin) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ padding: 3, marginTop: 8 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Unauthorized
          </Typography>
          <Typography variant="body1" align="center" sx={{ marginBottom: 2 }}>
            Sorry, you are not an authorized admin.
          </Typography>
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Render the admin view.
  return (
    <Container maxWidth="lg" sx={{ marginTop: 4 }}>
      <AdminLoggedIn />
      <Box display="flex" justifyContent="flex-end" sx={{ marginTop: 2 }}>
        <Button variant="outlined" color="secondary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
    </Container>
  );
}
